import { Grid, MenuItem, Select, SelectChangeEvent, TextField, Typography } from "@mui/material";
import { Mode } from "../../components/ReadWriteTextField";
import { ChangeEvent, useEffect, useState } from "react";
import { SectionHead } from "./SectionHead";
import { useAuthContext } from "AuthContextWrapper"
import { RelationshipOptions } from "../flows/OnBoarding/RelationshipScreen";
import { PROBATE_IDS } from "../../constants";
import { useProbateContext } from "ProbateContextWrapper";
import { User } from "models/User";
import { UserProfileAPI } from "apis/UserProfileAPI";
import { UserProfile } from "models/UserProfile";
import { UserAPI } from "apis/UserAPI";
import AddressAutocompleteTextField, { PlaceType } from "components/AddressAutocompleteTextField";
import { ReadWriteWrapper } from "components/ReadWriteWrapper";

interface IProps {
  allowedModes?: Mode[],
  startMode?: Mode,
}

export function ExecutorInfo(props:IProps):JSX.Element {
  const { user, setUser,countries,states } = useAuthContext()
  const [myUser, setMyUser] = useState<User>()
  const [myUserProfile, setMyUserProfile] = useState<UserProfile>()
  const [mode, setMode] = useState<Mode>(props.startMode || "read")
  const [loadingFieldValues, setLoadingFieldValues] = useState<boolean>(true)
  const [error, setError] = useState<string>("")
  const [savedSuccessfully, setSavedSuccessfully] = useState<boolean | null>(null)
  const [userProfileAtEditStart, setUserProfileAtEditStart] = useState<UserProfile | undefined>()
  const [userAtEditStart, setUserAtEditStart] = useState<User | undefined>()
  const [relationshipToDeceased, setRelationshipToDeceased] = useState<string>("")
  const [relationshipToDeceasedAtEditStart, setRelationshipToDeceasedAtEditStart] = useState<string>("")
  const {getFieldAnswerForUser, storeFieldAnswered} =  useProbateContext()
  
  function canEdit(): boolean {
    if(!props.allowedModes) {
      return true
    }

    return !!props.allowedModes?.find(m => m === "edit")
  }

  useEffect(() => {
    if(!user) {
      return
    }
    
    let profile:UserProfile|null = null
    if(user.profile) {
      profile = UserProfile.fromJSON(user.profile.toJSON())
      profile.userId = user.id
    } else {
      profile = new UserProfile(undefined, undefined, undefined, undefined, undefined, user.id)
    }
    setMyUserProfile(profile)

    const myUser = User.fromJSON(user.toJson())
    myUser.profile = profile
    setMyUser(myUser)
  }, [user?.id])

  useEffect(() => {
    async function getKnowledgeFieldAnswers() {
      const answerValue = await getFieldAnswerForUser(PROBATE_IDS.relationshipToDeceased)
      if(typeof answerValue === "string") {
        setRelationshipToDeceased(answerValue)
      }
    }

    getKnowledgeFieldAnswers()
  }, [])

  async function save() {
    if(!myUser || !myUserProfile) {
      setError("not done loading user yet")
      return
    }

    try {
      setError("")
      setSavedSuccessfully(null)

      // Store everything async
      await Promise.all([
        storeFieldAnswered({
          id: PROBATE_IDS.relationshipToDeceased,
          answer: relationshipToDeceased || undefined,
          answerValue:  relationshipToDeceased || undefined,
          prefix: "",
          getOptionLabel: () => undefined,
        }),
        UserProfileAPI.update(myUserProfile.toJSON()),
        UserAPI.updatePhone(myUser.phone || ""),
      ])

      const userClone = User.fromJSON(myUser.toJson())
      userClone.id = myUser.id 
      userClone.profile = myUserProfile
      setUser(userClone)

      setSavedSuccessfully(true)
      setUserProfileAtEditStart(myUserProfile)
      setUserAtEditStart(myUser)
      setRelationshipToDeceasedAtEditStart(relationshipToDeceased)
      setMode("read")
      setError("")
    } catch(e) {
      setError("unable to save estate: " + (e as any)?.message)
    }
  }


  return <div>
    <SectionHead mode={mode} 
      canEdit={canEdit()} 
      header={"Executor Info"}  
      message={
        savedSuccessfully ? <Typography variant="body1" color="primary">Saved successfully</Typography> :
        error ? <Typography variant="body1" color="error">{error}</Typography> :
        <></>
      }
      onCancel={() => {
        setMode("read")
        setSavedSuccessfully(null)
        setMyUserProfile(userProfileAtEditStart)
        setMyUser(userAtEditStart)
        setRelationshipToDeceased(relationshipToDeceasedAtEditStart)
      }}
      onEdit={() => {
        if(!myUserProfile || !myUser) {
          setError("not yet done loading my user")
          return
        }

        const clone = UserProfile.fromJSON(myUserProfile.toJSON())
        clone.id = myUserProfile.id
        setUserProfileAtEditStart(clone)

        const userClone = User.fromJSON(myUser.toJson())
        userClone.id = myUser.id
        setUserAtEditStart(userClone)

        setSavedSuccessfully(null)
        setRelationshipToDeceasedAtEditStart(relationshipToDeceased)
        setMode("edit")
      }}
      onSave={() => save()}
    />

    <Grid container style={{marginTop:"1rem"}}>
      <Grid item xs={12} sm={4} md={4} lg={4} xl={4} style={{paddingRight:"1rem"}}>
        <Typography variant="body1" component="p" style={{marginBottom:"0.5rem", fontWeight:"bold"}}>
          First Name
        </Typography>
      
        <ReadWriteWrapper
          value={myUserProfile?.firstName}
          mode={mode}
          blankReadOnlyValue="--"
        >
        <TextField 
          value={myUserProfile?.firstName}
          size="small"
          fullWidth
          onChange={(e) => {
            if(!myUserProfile || !myUser) {
              return
            }

            const clone = UserProfile.fromJSON(myUserProfile.toJSON())
            clone.id = myUserProfile.id
            clone.firstName = e.target.value
            setMyUserProfile(clone)

            const userClone = User.fromJSON(myUser.toJson())
            userClone.id = myUser.id
            userClone.profile = clone
            setMyUser(userClone)
          }} />
          </ReadWriteWrapper>
      </Grid>
      <Grid item xs={12} sm={3} md={3} lg={3} xl={3} style={{paddingRight:"1rem"}}>
        <Typography variant="body1" component="p" style={{marginBottom:"0.5rem", fontWeight:"bold"}}>
          Middle Name
        </Typography>
        <ReadWriteWrapper
          value={myUserProfile?.middleName}
          mode={mode}
          blankReadOnlyValue="--"
        >
        <TextField 
          value={myUserProfile?.middleName}
          size="small"
          fullWidth
          onChange={(e) => {
            if(!myUserProfile || !myUser) {
              return
            }

            const clone = UserProfile.fromJSON(myUserProfile.toJSON())
            clone.id = myUserProfile.id
            clone.middleName = e.target.value
            setMyUserProfile(clone)

            const userClone = User.fromJSON(myUser.toJson())
            userClone.id = myUser.id
            userClone.profile = clone
            setMyUser(userClone)
          }} />
          </ReadWriteWrapper>
      </Grid>
      <Grid item xs={12} sm={4} md={4} lg={4} xl={4} style={{paddingRight:"1rem"}}>
        <Typography variant="body1" component="p" style={{marginBottom:"0.5rem", fontWeight:"bold"}}>
          Last Name
        </Typography>
        <ReadWriteWrapper
          value={myUserProfile?.lastName}
          mode={mode}
          blankReadOnlyValue="--"
        >
        <TextField 
          value={myUserProfile?.lastName}
          fullWidth
          size="small"
          onChange={(e) => {
            if(!myUserProfile || !myUser) {
              return
            }

            const clone = UserProfile.fromJSON(myUserProfile.toJSON())
            clone.id = myUserProfile.id
            clone.lastName = e.target.value
            setMyUserProfile(clone)

            const userClone = User.fromJSON(myUser.toJson())
            userClone.id = myUser.id
            userClone.profile = clone
            setMyUser(userClone)
          }} />
          </ReadWriteWrapper>
      </Grid>
    </Grid>

    
    <Grid container style={{marginTop:"1rem"}}>
      <Grid item xs={12} sm={6} md={6} lg={6} xl={6} style={{paddingRight:"1rem"}}>
        <Typography variant="body1" component="p" style={{marginBottom:"0.5rem", fontWeight:"bold"}}>
          Email Address
        </Typography>
        <ReadWriteWrapper
          value={myUser?.email}
          mode={"read"} // read-only until we are ready with an endpoint that can update this
          blankReadOnlyValue="--"
        >
        <TextField 
          value={myUser?.email}
          fullWidth
          size="small"
          onChange={(e) => {
            if(!myUser) {
              return
            }

            const userClone = User.fromJSON(myUser.toJson())
            userClone.id = myUser.id
            userClone.email = e.target.value
            setMyUser(userClone)
          }} />
          </ReadWriteWrapper>
      </Grid>
    </Grid>


    <Grid container style={{marginTop:"1rem"}}>
      <Grid item xs={12} sm={6} md={6} lg={6} xl={6} style={{paddingRight:"1rem"}}>
        <Typography variant="body1" component="p" style={{marginBottom:"0.5rem", fontWeight:"bold"}}>
          Phone number
        </Typography>
      
        <ReadWriteWrapper
          value={myUser?.phone}
          mode={"read"} // TODO make editable once we can clear this field
          blankReadOnlyValue="--"
        >
        <TextField 
          value={myUser?.phone}
          fullWidth
          size="small"
          onChange={(e) => {
            if(!myUser) {
              return
            }

            const userClone = User.fromJSON(myUser.toJson())
            userClone.id = myUser.id
            userClone.phone = e.target.value
            setMyUser(userClone)
          }} />
          </ReadWriteWrapper>
      </Grid>
    </Grid>

    <Grid container style={{marginTop:"1rem"}}>
      <Grid item xs={12} sm={8} md={8} lg={6} xl={6} style={{paddingRight:"1rem"}}>
        <Typography variant="body1" component="p" style={{marginBottom:"0.5rem", fontWeight:"bold"}}>
          Street address
        </Typography>
        {myUserProfile && <ReadWriteWrapper
          value={myUserProfile?.addressOne}
          mode={"read"} // TODO make editable once we can clear this field
          blankReadOnlyValue="--"
        ><AddressAutocompleteTextField
          initialValue={myUserProfile?.addressOne}
          onPlaceChange={(place?:PlaceType) => { 
            if(!myUser || !myUserProfile) {
              return
            }

            const clone = UserProfile.fromJSON(myUserProfile.toJSON())
            clone.id = myUserProfile.id
            if(place) {
              clone.addressOne = place.structured_formatting.main_text +
                (place.structured_formatting.secondary_text ? ", " + place.structured_formatting.secondary_text : "")
            } else {
              clone.addressOne = undefined
            }
            setMyUserProfile(clone)

            const userClone = User.fromJSON(myUser.toJson())
            userClone.id = myUser.id
            userClone.profile = clone
            setMyUser(userClone)
          }}
        /></ReadWriteWrapper>}
      </Grid>
    </Grid> 

    {/* 
    Country doesn't exist on the profile object itself, somehow, but is save-able to it.
    State doesn't exist on user or profile. (It is on estate though)

    
    <Grid container style={{marginTop:"1rem"}}>
      <Grid item xs={12} sm={4} md={4} lg={4} xl={4} style={{paddingRight:"1rem"}}>
        <Typography variant="body1" component="p" style={{marginBottom:"0.5rem", fontWeight:"bold"}}>
          Country
        </Typography>
        <ReadWriteWrapper
          value={countries?.find(c => myUserProfile?.countryId == c.id.toString())?.name}
          mode={mode}
          blankReadOnlyValue="--"
        >
        <SelectCountry
          size="small"
          countryId={myUserProfile?.countryId || ""}
          onCountryIdChange={(id:string) => {
            if(!myUser || !myUserProfile) {
              return
            }

            const clone = UserProfile.fromJSON(myUserProfile.toJSON())
            clone.id = myUserProfile.id
            clone.countryId = id
            setMyUserProfile(clone)

            const userClone = User.fromJSON(myUser.toJson())
            userClone.id = myUser.id
            userClone.countryId = parseInt(id || "0")
            userClone.profile = clone
            setMyUser(userClone)
          }}
        />
        </ReadWriteWrapper>
      </Grid>
      <Grid item xs={12} sm={4} md={4} lg={4} xl={4} style={{paddingRight:"1rem"}}>
        <Typography variant="body1" component="p" style={{marginBottom:"0.5rem", fontWeight:"bold"}}>
          State
        </Typography>
        <ReadWriteWrapper
          value={states?.find((s:State) => myUserProfile?.stateId == s.id.toString())?.name}
          mode={mode}
          blankReadOnlyValue="--"
        >
        <SelectState 
          disabled={!myUserProfile?.countryId}
          size="small"
          stateId={myUserProfile?.stateId || ""}
          onStateIdChange={(id:string) => {
            if(!myUser || !myUserProfile) {
              return
            }

            const clone = UserProfile.fromJSON(myUserProfile.toJSON())
            clone.id = myUserProfile.id
            clone.stateId = id
            setMyUserProfile(clone)

            const userClone = User.fromJSON(myUser.toJson())
            userClone.id = myUser.id
            userClone.profile = clone
            setMyUser(userClone)
          }}
          allowedCountryIds={myUser?.countryId ? [myUser.countryId] : undefined}
          />
          </ReadWriteWrapper>
      </Grid>
    </Grid> */}

    <Grid container style={{marginTop:"1rem"}}>
      <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
        <Typography variant="body1" component="p" style={{marginBottom:"0.5rem", fontWeight:"bold"}}>
          Relationship to deceased
        </Typography>
        <ReadWriteWrapper
          value={relationshipToDeceased}
          mode={mode}
          blankReadOnlyValue="--"
        >
          <Select
            size ="small"
            readOnly={mode !== "edit"}
            fullWidth
            onChange={(e:SelectChangeEvent) => {
              setRelationshipToDeceased(e.target.value.toString())
              return true
            }}
            value={relationshipToDeceased}
          >
            {RelationshipOptions.map((option,index) => {
                return <MenuItem 
                  key={"option" + index}
                  value={option.value.toString()} 
                >{option.label}</MenuItem> 
              })}
          </Select>
        </ReadWriteWrapper>
      </Grid>
    </Grid>
  </div>
}